import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { reportError } from '../lib/error-reporting';

interface Props {
  children: ReactNode;
  /** Optional callback invoked when an error is caught. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Fallback UI displayed when an unhandled render-phase error occurs.
 * Wraps the tree with Sentry.ErrorBoundary (for automatic breadcrumb capture)
 * and our own fallback UI.
 */
class InnerErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError(error, {
      context: 'ErrorBoundary',
      componentStack: errorInfo.componentStack || undefined,
    });

    this.props.onError?.(error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="text-center max-w-md">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-red-900 mb-2">
                Une erreur s'est produite
              </h2>
              <p className="text-red-700 mb-4">
                L'application a rencontr&eacute; une erreur inattendue.
              </p>
              {this.state.error && (
                <p className="text-sm text-red-600 mb-4 font-mono bg-red-100 p-2 rounded">
                  {this.state.error.message}
                </p>
              )}
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.href = '/';
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded mr-2"
              >
                Retour à l'accueil
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Public ErrorBoundary that wraps InnerErrorBoundary with Sentry.ErrorBoundary
 * for automatic Sentry dialog / breadcrumb support.
 */
export function ErrorBoundary({ children, onError }: Props) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="text-center max-w-md">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-red-900 mb-2">
                Une erreur s'est produite
              </h2>
              <p className="text-red-700 mb-4">
                L'application a rencontr&eacute; une erreur inattendue.
              </p>
              {error instanceof Error && (
                <p className="text-sm text-red-600 mb-4 font-mono bg-red-100 p-2 rounded">
                  {error.message}
                </p>
              )}
              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded mr-2"
              >
                Retour à l'accueil
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      )}
    >
      <InnerErrorBoundary onError={onError}>
        {children}
      </InnerErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}
